import React, { useRef } from "react";
import { motion } from "motion/react";
import "./MagicBento.css";

interface MagicBentoProps {
    children?: React.ReactNode;
    className?: string; // Additional classes
    textAutoHide?: boolean;
    enableStars?: boolean;
    enableSpotlight?: boolean;
    enableBorderGlow?: boolean;
    enableTilt?: boolean; // Not implemented in CSS ref
    enableMagnetism?: boolean; // Not implemented in CSS ref
    clickEffect?: boolean;
    spotlightRadius?: number;
    particleCount?: number;
    glowColor?: string; // Assuming CSS handles color mostly, or we inject via style
    disableAnimations?: boolean;
    onClick?: () => void;
}

const MagicBento: React.FC<MagicBentoProps> = ({
    children,
    className = "",
    textAutoHide = false,
    enableStars = false,
    enableSpotlight = true,
    enableBorderGlow = true,
    clickEffect = false,
    spotlightRadius = 200,
    particleCount = 12,
    glowColor = "132, 0, 255", // Only used if we want to override CSS vars
    disableAnimations = false,
    onClick,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if (disableAnimations || !containerRef.current) return;

        const rect = containerRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        containerRef.current.style.setProperty("--glow-x", `${x}px`);
        containerRef.current.style.setProperty("--glow-y", `${y}px`);
        containerRef.current.style.setProperty("--glow-intensity", "1");
    };

    const handleMouseLeave = () => {
        if (containerRef.current) {
            containerRef.current.style.setProperty("--glow-intensity", "0");
        }
    }

    return (
        <div
            ref={containerRef}
            className={`magic-bento-card ${enableBorderGlow ? 'magic-bento-card--border-glow' : ''} ${textAutoHide ? 'magic-bento-card--text-autohide' : ''} ${className}`}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onClick={onClick}
            style={{
                "--glow-radius": `${spotlightRadius}px`,
                // We could overriding colors if needed:
                // "--purple-primary": `rgba(${glowColor}, 1)`,
            } as React.CSSProperties}
        >
            {/* Stars */}
            {enableStars && !disableAnimations && (
                <div className="particle-container">
                    {Array.from({ length: particleCount }).map((_, i) => (
                        <Star key={i} />
                    ))}
                </div>
            )}

            {/* Global Spotlight (optional, if we want inner spotlight in addition to border glow) */}
            {enableSpotlight && !disableAnimations && (
                <div className="absolute inset-0 pointer-events-none"
                    style={{
                        background: `radial-gradient(var(--glow-radius) circle at var(--glow-x) var(--glow-y), rgba(${glowColor}, 0.15), transparent)`,
                        opacity: "var(--glow-intensity)",
                        transition: "opacity 0.3s ease"
                    }}
                />
            )}

            {children}
        </div>
    );
};

const Star = () => {
    const top = Math.random() * 100;
    const left = Math.random() * 100;
    const duration = 2 + Math.random() * 3;
    const delay = Math.random() * 2;

    return (
        <motion.div
            className="absolute rounded-full bg-white"
            style={{
                top: `${top}%`,
                left: `${left}%`,
                width: "2px",
                height: "2px",
                boxShadow: "0 0 2px rgba(255,255,255,0.8)"
            }}
            animate={{
                opacity: [0, 1, 0],
                scale: [0.5, 1.5, 0.5]
            }}
            transition={{
                duration: duration,
                repeat: Infinity,
                delay: delay,
                ease: "easeInOut"
            }}
        />
    )
}

export default MagicBento;
